import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastDescriptor {
  id: string;
  message: string;
  detail?: string;
  tone?: ToastTone;
  autoClose?: number | false; // false means don't auto-close
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: ToastDescriptor[];
  pushToast: (toast: Omit<ToastDescriptor, 'id'> & { id?: string }) => string;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const generateId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastDescriptor[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback(
    (toast: Omit<ToastDescriptor, 'id'> & { id?: string }) => {
      const id = toast.id ?? generateId();
      const payload: ToastDescriptor = {
        tone: 'info',
        autoClose: 5000,
        ...toast,
        id,
      };
      setToasts((prev) => [...prev, payload]);
      const timeout = payload.autoClose;
      if (timeout && typeof timeout === 'number' && timeout > 0) {
        setTimeout(() => dismissToast(id), timeout);
      }
      return id;
    },
    [dismissToast],
  );

  const value = useMemo(
    () => ({
      toasts,
      pushToast,
      dismissToast,
    }),
    [toasts, pushToast, dismissToast],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToasts = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToasts must be used within ToastProvider');
  }
  return ctx;
};
